import {
  Edit,
  SimpleForm,
  TextInput,
  Datagrid,
  List,
  TextField,
  Create,
  TabbedForm,
  FormTab,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  DateInput,
  BooleanInput,
  ChipField,
  FunctionField,
  DateField,
  useCreateContext,
  FormDataConsumer,
  ReferenceManyField,
  useRecordContext,
} from "react-admin";
import { Grid, Chip, Box, Typography, Card, CardContent, CardMedia, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { useState } from "react";
import { deviceTemplates, getDeviceTemplate, getTemplateChoices } from "./device-templates";

// Device type choices matching backend enum
const deviceTypeChoices = [
  { id: 'pos', name: 'POS Terminal' },
  { id: 'payment', name: 'Payment Device' },
  { id: 'printer', name: 'Printer' },
  { id: 'kiosk', name: 'Self-Service Kiosk' },
  { id: 'mobile', name: 'Mobile Device' },
  { id: 'tablet', name: 'Tablet' },
  { id: 'kds', name: 'Kitchen Display System' },
  { id: 'scale', name: 'Scale' },
  { id: 'scanner', name: 'Scanner' },
  { id: 'display', name: 'Customer Display' },
  { id: 'cash_drawer', name: 'Cash Drawer' },
  { id: 'server', name: 'Server' },
  { id: 'router', name: 'Router' },
  { id: 'access_point', name: 'Access Point' },
  { id: 'camera', name: 'Camera' },
  { id: 'other', name: 'Other' },
];

const deviceStatusChoices = [
  { id: 'active', name: 'Active' },
  { id: 'inactive', name: 'Inactive' },
  { id: 'maintenance', name: 'Maintenance' },
  { id: 'offline', name: 'Offline' },
  { id: 'error', name: 'Error' },
  { id: 'decommissioned', name: 'Decommissioned' },
];

// Health Tab - Device health metrics and logs
const DeviceHealthTabContent = () => {
  const record = useRecordContext();
  
  return (
    <Box>
      {/* Health Metrics */}
      <Typography variant="h6" gutterBottom>
        Health Metrics
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6}>
          <DateInput source="health.lastHeartbeat" label="Last Heartbeat" fullWidth disabled />
        </Grid>
        <Grid item xs={6}>
          <TextInput source="health.uptime" label="Uptime (seconds)" type="number" fullWidth disabled />
        </Grid>
        <Grid item xs={3}>
          <TextInput source="health.batteryLevel" label="Battery %" type="number" fullWidth disabled />
        </Grid>
        <Grid item xs={3}>
          <TextInput source="health.cpuUsage" label="CPU %" type="number" fullWidth disabled />
        </Grid>
        <Grid item xs={3}>
          <TextInput source="health.memoryUsage" label="Memory %" type="number" fullWidth disabled />
        </Grid>
        <Grid item xs={3}>
          <TextInput source="health.storageUsage" label="Storage %" type="number" fullWidth disabled />
        </Grid>
      </Grid>
      
      {/* Errors and Warnings */}
      {(record?.health?.errors?.length > 0 || record?.health?.warnings?.length > 0) && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Issues
          </Typography>
          
          {record.health.errors?.length > 0 && (
            <Card sx={{ mb: 2, bgcolor: '#fee', border: '1px solid #fcc' }}>
              <CardContent>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Errors ({record.health.errors.length})
                </Typography>
                {record.health.errors.map((error: string, idx: number) => (
                  <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', color: '#c00' }}>
                    • {error}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          )}
          
          {record.health.warnings?.length > 0 && (
            <Card sx={{ mb: 2, bgcolor: '#fffbeb', border: '1px solid #ffc' }}>
              <CardContent>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  Warnings ({record.health.warnings.length})
                </Typography>
                {record.health.warnings.map((warning: string, idx: number) => (
                  <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', color: '#f59e0b' }}>
                    • {warning}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          )}
        </Box>
      )}
      
      {/* Device Logs */}
      <Typography variant="h6" gutterBottom>
        Device Logs
      </Typography>
      <Card sx={{ bgcolor: '#000', color: '#0f0', border: '1px solid #333' }}>
        <CardContent>
          <Box sx={{ 
            fontFamily: 'monospace', 
            fontSize: '12px',
            maxHeight: '400px',
            overflow: 'auto',
            p: 2,
            bgcolor: '#0a0a0a',
            borderRadius: 1
          }}>
            {/* Mock logs - in production these would come from a logs API */}
            <Box sx={{ mb: 0.5 }}>
              <Typography component="span" sx={{ color: '#666', fontFamily: 'monospace', fontSize: '12px' }}>
                {new Date().toISOString()}
              </Typography>
              <Typography component="span" sx={{ color: '#4fc3f7', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                [INFO]
              </Typography>
              <Typography component="span" sx={{ color: '#0f0', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                Device heartbeat received - Status: OK
              </Typography>
            </Box>
            <Box sx={{ mb: 0.5 }}>
              <Typography component="span" sx={{ color: '#666', fontFamily: 'monospace', fontSize: '12px' }}>
                {new Date(Date.now() - 60000).toISOString()}
              </Typography>
              <Typography component="span" sx={{ color: '#4fc3f7', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                [INFO]
              </Typography>
              <Typography component="span" sx={{ color: '#0f0', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                Health check completed - CPU: {record?.health?.cpuUsage || 0}%, Memory: {record?.health?.memoryUsage || 0}%
              </Typography>
            </Box>
            {record?.health?.errors?.map((error: string, idx: number) => (
              <Box key={idx} sx={{ mb: 0.5 }}>
                <Typography component="span" sx={{ color: '#666', fontFamily: 'monospace', fontSize: '12px' }}>
                  {new Date(Date.now() - (idx + 1) * 120000).toISOString()}
                </Typography>
                <Typography component="span" sx={{ color: '#f44336', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                  [ERROR]
                </Typography>
                <Typography component="span" sx={{ color: '#f88', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                  {error}
                </Typography>
              </Box>
            ))}
            {record?.health?.warnings?.map((warning: string, idx: number) => (
              <Box key={idx} sx={{ mb: 0.5 }}>
                <Typography component="span" sx={{ color: '#666', fontFamily: 'monospace', fontSize: '12px' }}>
                  {new Date(Date.now() - (idx + 1) * 90000).toISOString()}
                </Typography>
                <Typography component="span" sx={{ color: '#ff9800', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                  [WARN]
                </Typography>
                <Typography component="span" sx={{ color: '#fb3', fontFamily: 'monospace', fontSize: '12px', ml: 1 }}>
                  {warning}
                </Typography>
              </Box>
            ))}
            
            {/* Placeholder when no logs */}
            {(!record?.health?.errors || record.health.errors.length === 0) && 
             (!record?.health?.warnings || record.health.warnings.length === 0) && (
              <Typography sx={{ color: '#666', fontFamily: 'monospace', fontSize: '12px' }}>
                No recent errors or warnings. Device is operating normally.
              </Typography>
            )}
          </Box>
          
          <Box sx={{ mt: 2, p: 1, bgcolor: '#1a1a1a', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              💡 Real-time logs will be available when device monitoring is active. Above shows recent health check results.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// Deployments Tab for Device - Shows what's deployed on this device
const DeploymentsTabContent = () => {
  const record = useRecordContext();
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the device first to view deployments
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Applications Deployed to This Device
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          View all applications installed on this device, their versions, and deployment status
        </Typography>
      </Box>
      
      <ReferenceManyField
        label=""
        reference="application-deployment"
        target="deviceId"
        sort={{ field: 'deployedAt', order: 'DESC' }}
      >
        <Datagrid bulkActionButtons={false}>
          <FunctionField
            label="Application"
            render={(record: any) => (
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {record.applicationKey || record.applicationId}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Version {record.applicationVersion} {record.applicationVersionLabel ? `(${record.applicationVersionLabel})` : ''}
                </Typography>
              </Box>
            )}
          />
          <FunctionField
            label="Status"
            render={(record: any) => {
              const statusColors: Record<string, any> = {
                installed: 'success',
                installing: 'info',
                pending: 'default',
                failed: 'error',
                outdated: 'warning',
                updating: 'info',
              };
              return (
                <Chip 
                  label={record.status} 
                  color={statusColors[record.status] || 'default'} 
                  size="small" 
                />
              );
            }}
          />
          <FunctionField
            label="Sync Status"
            render={(record: any) => {
              const syncColors: Record<string, any> = {
                synced: 'success',
                outdated: 'warning',
                conflict: 'error',
                'local-only': 'default',
                'pending-sync': 'info',
              };
              return (
                <Chip 
                  label={record.syncStatus} 
                  color={syncColors[record.syncStatus] || 'default'} 
                  size="small" 
                  variant="outlined"
                />
              );
            }}
          />
          <DateField source="deployedAt" label="Deployed" showTime />
          <TextField source="deployedBy" label="Deployed By" />
          <FunctionField
            label="Health"
            render={(record: any) => {
              if (!record.health?.isRunning) return <Chip label="Not Running" size="small" />;
              const hasErrors = record.health.errors?.length > 0;
              return (
                <Chip 
                  label={hasErrors ? `Errors (${record.health.errors.length})` : 'Running'} 
                  color={hasErrors ? 'error' : 'success'} 
                  size="small" 
                />
              );
            }}
          />
        </Datagrid>
      </ReferenceManyField>
      
      {/* Info card about deployments */}
      <Card sx={{ mt: 2, bgcolor: '#1a202c', color: '#e2e8f0', border: '1px solid #2d3748' }}>
        <CardContent>
          <Typography variant="body2" sx={{ color: '#cbd5e0' }}>
            💡 <strong>Tip:</strong> Applications are deployed at the Location or Brand level, then automatically installed on matching devices. 
            To deploy new applications to this device, go to the Location or Brand editor.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export const DeviceList = () => (
  <List>
    <Datagrid rowClick="edit">
      <TextField source="name" />
      <ChipField source="deviceType" />
      <ChipField source="status" />
      <TextField source="hardware.manufacturer" label="Manufacturer" />
      <TextField source="hardware.model" label="Model" />
      <TextField source="hardware.serialNumber" label="Serial #" />
      <FunctionField
        label="Health"
        render={(record: any) => {
          if (!record.health?.lastHeartbeat) return '-';
          const lastSeen = new Date(record.health.lastHeartbeat);
          const minutesAgo = Math.floor((Date.now() - lastSeen.getTime()) / 60000);
          const color = minutesAgo < 5 ? 'success' : minutesAgo < 30 ? 'warning' : 'error';
          return <Chip label={`${minutesAgo}m ago`} color={color} size="small" />;
        }}
      />
    </Datagrid>
  </List>
);

export const DeviceEdit = () => (
  <Edit>
    <TabbedForm>
      <FormTab label="General">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput source="name" fullWidth required />
          </Grid>
          <Grid item xs={6}>
            <SelectInput source="deviceType" choices={deviceTypeChoices} fullWidth required />
          </Grid>
          <Grid item xs={6}>
            <SelectInput source="status" choices={deviceStatusChoices} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextInput source="description" fullWidth multiline rows={3} />
          </Grid>
        </Grid>
      </FormTab>

      <FormTab label="Assignment">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Hierarchy: Tenant → Brand → Location (optional)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <ReferenceInput source="tenantId" reference="tenant" label="Tenant" required>
              <AutocompleteInput optionText="name" fullWidth />
            </ReferenceInput>
          </Grid>
          <Grid item xs={6}>
            <ReferenceInput source="brandId" reference="brand" label="Brand" required>
              <AutocompleteInput optionText="name" fullWidth />
            </ReferenceInput>
          </Grid>
          <Grid item xs={6}>
            <ReferenceInput source="locationId" reference="location" label="Location (Optional)">
              <AutocompleteInput optionText="name" fullWidth />
            </ReferenceInput>
          </Grid>
        </Grid>
      </FormTab>

      <FormTab label="Hardware">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput source="hardware.manufacturer" label="Manufacturer" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="hardware.model" label="Model" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="hardware.serialNumber" label="Serial Number" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="hardware.firmwareVersion" label="Firmware Version" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <TextInput source="hardware.processor" label="Processor" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <TextInput source="hardware.memory" label="Memory" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <TextInput source="hardware.storage" label="Storage" fullWidth />
          </Grid>
        </Grid>
      </FormTab>

      <FormTab label="Software">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput source="software.osType" label="OS Type" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="software.osVersion" label="OS Version" fullWidth />
          </Grid>
          <Grid item xs={12}>
            <DateInput source="software.lastUpdateCheck" label="Last Update Check" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <BooleanInput source="software.updateAvailable" label="Update Available" />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="software.targetVersion" label="Target OS Version" fullWidth />
          </Grid>
        </Grid>
      </FormTab>

      <FormTab label="Network">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextInput source="network.ipAddress" label="IP Address" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="network.macAddress" label="MAC Address" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <TextInput source="network.hostname" label="Hostname" fullWidth />
          </Grid>
          <Grid item xs={6}>
            <SelectInput 
              source="network.connectionType" 
              label="Connection Type"
              choices={[
                { id: 'wifi', name: 'WiFi' },
                { id: 'ethernet', name: 'Ethernet' },
                { id: 'cellular', name: 'Cellular' },
              ]}
              fullWidth 
            />
          </Grid>
          <Grid item xs={8}>
            <TextInput source="network.ssid" label="WiFi SSID" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <TextInput source="network.signalStrength" label="Signal %" type="number" fullWidth />
          </Grid>
        </Grid>
      </FormTab>

      <FormTab label="Health">
        <DeviceHealthTabContent />
      </FormTab>

      <FormTab label="Publishing">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2, p: 2, bgcolor: '#1a202c', color: '#e2e8f0', borderRadius: 1, border: '1px solid #2d3748' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#4fc3f7' }}>Publishing & Versioning</Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e0' }}>
                Publish this device configuration to make it available for syncing to child instances
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <BooleanInput source="published" label="Published" />
          </Grid>
          <Grid item xs={3}>
            <TextInput source="versionLabel" label="Version Label" fullWidth />
          </Grid>
          <Grid item xs={3}>
            <TextInput source="currentVersion" label="Current Version" fullWidth disabled />
          </Grid>
          <Grid item xs={3}>
            <DateField source="publishedAt" label="Published At" showTime />
          </Grid>
          <Grid item xs={12}>
            <BooleanInput source="isManaged" label="Managed by Parent Instance" />
          </Grid>
          <Grid item xs={12}>
            <BooleanInput source="allowRemotePush" label="Allow Remote Push Updates" />
          </Grid>
        </Grid>
      </FormTab>

      <FormTab label="Deployments">
        <DeploymentsTabContent />
      </FormTab>
      
      <FormTab label="Notes">
        <TextInput source="notes" fullWidth multiline rows={8} />
      </FormTab>
    </TabbedForm>
  </Edit>
);

const DeviceTemplateSelector = ({ 
  selected, 
  onChange 
}: { 
  selected: string;
  onChange: (templateId: string, template: any) => void;
}) => {
  
  const handleChange = (templateId: string) => {
    const template = getDeviceTemplate(templateId);
    if (template) {
      onChange(templateId, template);
    }
  };
  
  const selectedTemplate = selected ? getDeviceTemplate(selected) : null;
  
  return (
    <Card sx={{ bgcolor: '#1a202c', color: '#e2e8f0', mb: 2, border: '1px solid #2d3748', position: 'relative' }}>
      {/* Device Image in Upper Right Corner */}
      {selectedTemplate?.media?.primaryImageUrl && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: 100,
            height: 100,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid #4fc3f7',
            boxShadow: '0 4px 12px rgba(79, 195, 247, 0.3)',
            zIndex: 1,
          }}
        >
          <CardMedia
            component="img"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            image={selectedTemplate.media.primaryImageUrl}
            alt={selectedTemplate.name}
          />
        </Box>
      )}
      
      <CardContent sx={{ pr: selectedTemplate?.media?.primaryImageUrl ? 14 : 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#4fc3f7' }}>
          Quick Start: Select a Device Template
        </Typography>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel sx={{ color: '#a0aec0' }}>Device Template</InputLabel>
          <Select
            value={selected}
            onChange={(e) => handleChange(e.target.value as string)}
            label="Device Template"
            sx={{ 
              bgcolor: '#2d3748', 
              color: '#e2e8f0',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4a5568',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4fc3f7',
              }
            }}
          >
            <MenuItem value="">
              <em>None - Manual Entry</em>
            </MenuItem>
            {deviceTemplates.map(t => (
              <MenuItem key={t.id} value={t.id}>
                {t.name} ({t.hardware.manufacturer})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selected && (
          <Box sx={{ mt: 2, bgcolor: '#2d3748', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#cbd5e0' }}>
              {selectedTemplate?.description}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const DeviceCreateForm = () => {
  const { record: stateRecord } = useCreateContext();
  const [defaultValues, setDefaultValues] = useState({});
  const [formKey, setFormKey] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  
  // Check if we have pre-populated values from Location context
  const hasPrePopulatedContext = !!(stateRecord?.tenantId || stateRecord?.brandId || stateRecord?.locationId);
  
  const applyTemplate = (templateId: string, template: any) => {
    setSelectedTemplateId(templateId);
    const newDefaults = {
      name: template.name,
      deviceType: template.deviceType,
      description: template.description,
      hardware: {
        manufacturer: template.hardware.manufacturer,
        model: template.hardware.model,
        firmwareVersion: template.hardware.firmwareVersion,
        processor: template.hardware.processor,
        memory: template.hardware.memory,
        storage: template.hardware.storage,
      },
      software: {
        osType: template.software?.osType,
        osVersion: template.software?.osVersion,
      },
      media: {
        primaryImageUrl: template.media?.primaryImageUrl,
      },
      // Preserve pre-populated context values
      ...(stateRecord?.tenantId && { tenantId: stateRecord.tenantId }),
      ...(stateRecord?.brandId && { brandId: stateRecord.brandId }),
      ...(stateRecord?.locationId && { locationId: stateRecord.locationId }),
    };
    setDefaultValues(newDefaults);
    setFormKey(prev => prev + 1); // Force form remount to mark as dirty
  };
  
  return (
    <SimpleForm key={formKey} defaultValues={{ ...stateRecord, ...defaultValues }}>
      <Grid container spacing={2}>
        {/* Template Selector */}
        <Grid item xs={12}>
          <DeviceTemplateSelector selected={selectedTemplateId} onChange={applyTemplate} />
        </Grid>
              
              {/* Basic Information */}
              <Grid item xs={12}>
                <TextInput source="name" fullWidth required />
              </Grid>
              <Grid item xs={6}>
                <SelectInput source="deviceType" choices={deviceTypeChoices} fullWidth required />
              </Grid>
              <Grid item xs={6}>
                <SelectInput source="status" choices={deviceStatusChoices} fullWidth defaultValue="inactive" />
              </Grid>
              <Grid item xs={12}>
                <TextInput source="description" fullWidth multiline rows={3} />
              </Grid>
              
              {/* Assignment - Hierarchy: Tenant > Brand > Location */}
              <Grid item xs={12}>
                {hasPrePopulatedContext ? (
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    ✓ Auto-populated from current location context
                  </Typography>
                ) : (
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Assignment (select at least Tenant and Brand, optionally assign to a specific Location)
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <ReferenceInput source="tenantId" reference="tenant" label="Tenant" required>
                  <AutocompleteInput optionText="name" fullWidth disabled={!!stateRecord?.tenantId} />
                </ReferenceInput>
              </Grid>
              <Grid item xs={6}>
                <ReferenceInput source="brandId" reference="brand" label="Brand" required>
                  <AutocompleteInput optionText="name" fullWidth disabled={!!stateRecord?.brandId} />
                </ReferenceInput>
              </Grid>
              <Grid item xs={6}>
                <ReferenceInput source="locationId" reference="location" label="Location (Optional)">
                  <AutocompleteInput optionText="name" fullWidth disabled={!!stateRecord?.locationId} />
                </ReferenceInput>
              </Grid>
              
              {/* Hardware Details */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Hardware Specifications
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextInput source="hardware.manufacturer" label="Manufacturer" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="hardware.model" label="Model" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextInput source="hardware.serialNumber" label="Serial Number" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="hardware.firmwareVersion" label="Firmware Version" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="hardware.processor" label="Processor" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="hardware.memory" label="Memory" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="hardware.storage" label="Storage" fullWidth />
              </Grid>
              
              {/* Software */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Software Configuration
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextInput source="software.osType" label="OS Type" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextInput source="software.osVersion" label="OS Version" fullWidth />
              </Grid>
              
              {/* Media */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Device Image
                </Typography>
              </Grid>
        <Grid item xs={12}>
          <TextInput source="media.primaryImageUrl" label="Image URL" fullWidth />
        </Grid>
      </Grid>
    </SimpleForm>
  );
};

export const DeviceCreate = () => {
  return (
    <Create>
      <DeviceCreateForm />
    </Create>
  );
};
